// src/data/seo.js
//
// Central SEO manifest for routes whose title/description aren't carried
// in markdown frontmatter. Frontmatter-driven routes (articles, blog,
// guides, states) read metaTitle/metaDescription directly from the
// markdown file; cities are derived at render time from city data.
//
// What's in here:
//   - Homepage (1)
//   - Tool pages (17) — the interactive tool route /tools/<slug>
//   - Pillar landing pages (4) — encyclopedia, diagnostician, field-notes,
//     pro-track
//   - Index pages (5) — articles index, blog index, state index,
//     contributor index, glossary
//   - About / Trust pages (5) — editorial-standards, sources, contact,
//     submit, find-a-cleaner
//   - 404
//
// What's NOT in here:
//   - Methodology guide pages (/tools/<slug>/guide) — read from guide
//     frontmatter
//   - Article pages — read from article frontmatter
//   - Blog post pages — read from blog frontmatter
//   - State pages — read from state frontmatter (already has metaDescription)
//   - City pages — generated in CityPage.jsx from city data
//   - Contributor detail pages (/contributors/<slug>) — generated from
//     contributors.js data
//
// Length conventions (enforced by audit-seo-metadata.mjs):
//   - metaTitle: ≤ 60 chars (SERP truncation threshold)
//   - metaDescription: ≤ 160 chars (SERP truncation threshold)
//   - Both should be ≥ 30 / 120 chars respectively (under-length warns)
//
// Title pattern: <Specific Hook> | Window Washing Guide
//   for routes that benefit from the brand suffix. Homepage and a few
//   already-strong titles skip the suffix.

// SITE_NAME is defined once, in utils/pageTitle.js, alongside the rule that
// decides when it gets appended to a title. Re-exported here so the many
// existing `from '../data/seo.js'` importers keep working (s76).
export { SITE_NAME } from '../utils/pageTitle.js';
export const SITE_URL = 'https://windowwashingguide.com';

export const STATIC_SEO = {
  // ─── Homepage ──────────────────────────────────────────────────────
  // s79 (audit D9): the homepage now READS this entry — Homepage.jsx imports
  // it — so this is the one place the homepage title/description live.
  // Title carries the brand itself (58ch, so no suffix is appended) and the
  // two head terms the audit named: "window washing guide" / "how to clean
  // windows". Copy is Claude's reasonable call pending Giode's direction;
  // alternates are logged in HANDOFF.md. Edit here, nowhere else.
  '/': {
    metaTitle: 'Window Washing Guide: How to Clean Windows Without Streaks',
    metaDescription:
      'How to clean windows without streaks: 47 articles on hard water, foggy glass, scratches and squeegee technique, 23 diagnostic tools, and a 50-state water atlas.',
  },

  // ─── Tool pages ────────────────────────────────────────────────────
  '/tools/streak-diagnostic': {
    metaTitle: 'Streak Diagnostic — find what is wrong with your window',
    metaDescription:
      'Two-question visual diagnostic. Match the streak, haze, or spot pattern on your glass to one of twelve diagnoses and the article that walks you through the fix.',
  },
  '/tools/hard-water-scorer': {
    metaTitle: 'Hard Water Severity Scorer — by ZIP and utility',
    metaDescription:
      'Score the hard-water risk for your address. ZIP-level hardness, severity tier, and the cleaning protocol adjustments that match your water profile.',
  },
  '/tools/solution-calculator': {
    metaTitle: 'Solution Calculator — surfactant ratios that actually work',
    metaDescription:
      'Calculate the right cleaning solution for your glass, water hardness, and method. Print-ready ratios for spray bottles, buckets, and pure-water systems.',
  },
  '/tools/technique-library': {
    metaTitle: 'Technique Library — animated window-cleaning strokes',
    metaDescription:
      'An animated reference of squeegee strokes, edge work, and pole technique. Chapter-organized, with the working name and the failure mode each stroke prevents.',
  },
  '/tools/cost-estimator': {
    metaTitle: 'Window Cleaning Cost Estimator — by region',
    metaDescription:
      'Realistic price ranges for residential and commercial window cleaning, region-adjusted. Built from working-route rate data, not aggregator averages.',
  },
  '/tools/tint-identifier': {
    metaTitle: 'Tint & Coating Identifier — read glass before you clean',
    metaDescription:
      'Identify low-E, reflective, anti-reflective, and applied film coatings before you wash. The wrong solvent on the wrong coating is permanent damage.',
  },
  '/tools/frame-substrate-identifier': {
    metaTitle: 'Frame Substrate Identifier — vinyl, aluminum, wood, clad',
    metaDescription:
      'Identify the frame material before you choose a cleaner. Vinyl, aluminum, painted wood, and clad frames each have their own compatibility constraints.',
  },
  '/tools/igu-self-check': {
    metaTitle: 'IGU Self-Check — has the insulated glass seal failed',
    metaDescription:
      "A four-question self-check for insulated glass unit seal failure. Distinguishes condensation between panes from the surface fog that's actually fixable.",
  },
  '/tools/regional-protocol-generator': {
    metaTitle: 'Regional Protocol Generator — climate-aware cleaning',
    metaDescription:
      'Generate the right cleaning protocol for your region: water hardness, humidity, pollen wave, salt aerosol, and the seasonal timing that matters most.',
  },
  '/tools/cleaning-schedule-builder': {
    metaTitle: 'Cleaning Schedule Builder — calendar by region and use',
    metaDescription:
      "A working schedule for when to clean what, by region and exposure. Synthesizes pollen, salt, freeze risk, and use intensity into a realistic calendar.",
  },
  '/tools/solvent-ladder-selector': {
    metaTitle: 'Solvent Ladder Selector — mildest cleaner that works',
    metaDescription:
      'A working solvent ladder for stubborn marks: sap, tar, paint, label residue. Starts mildest, escalates only as needed, with substrate compatibility flagged.',
  },
  '/tools/heritage-glass-triage': {
    metaTitle: 'Heritage Glass Triage — is it pre-1920s window glass',
    metaDescription:
      "Triage for heritage glass before you clean it. Identifies cylinder, crown, and early float glass; flags the cleaning methods that will and won't damage them.",
  },
  '/tools/drying-window-predictor': {
    metaTitle: 'Drying Window Predictor — evaporation math by temp and RH',
    metaDescription:
      'Predict how long the working window is before solution flashes off. Inputs: air temp, RH, surface temp, wind. Output: minutes before streaks.',
  },
  '/tools/cold-weather-window-calculator': {
    metaTitle: 'Cold-Weather Window Calculator — freeze math',
    metaDescription:
      "Lowest air temperature you can safely wash at, by solution. Accounts for freeze-point depression and the wet-glass freeze risk that shows up later.",
  },
  '/tools/pole-reach-calculator': {
    metaTitle: 'Pole Reach Calculator — how high can you actually reach',
    metaDescription:
      "Reach math for pole work: working height, taper-off loss, body angle, and the control limit that's separate from the manufacturer's max-length spec.",
  },
  '/tools/salt-spray-interval-calculator': {
    metaTitle: 'Salt-Spray Interval Calculator — coastal cleaning frequency',
    metaDescription:
      'How often coastal windows need cleaning to stay ahead of salt aerosol. By distance from shore, prevailing wind, and surfactant residue carry.',
  },
  '/tools/pure-water-tds-diagnostic': {
    metaTitle: 'Pure-Water TDS Diagnostic — what your spot pattern means',
    metaDescription:
      'Diagnose pure-water-system spotting and streaking by TDS reading and pattern. Distinguishes resin exhaustion, RO membrane failure, and post-rinse contamination.',
  },
  '/tools/squeegee-rubber-tracker': {
    metaTitle: 'Squeegee Rubber Tracker — when to flip or replace',
    metaDescription:
      'Track squeegee-rubber life across hours, chemistry, climate, glass, brand, and storage. Tells you when to flip, when to replace, and when the channel is wrong.',
  },
  '/tools/bird-soiling-urgency': {
    metaTitle: 'Bird-Soiling Urgency Calculator — chemistry clock',
    metaDescription:
      'How urgent is the bird mess on your window? Chemistry-based clock that says immediate, today, this week, or whenever. Plus the removal protocol for your glass.',
  },
  '/tools/storefront-frequency': {
    metaTitle: 'Storefront Frequency Calculator — the cadence math',
    metaDescription:
      'How often should this storefront be cleaned? Math-driven cadence by foot traffic, adjacent businesses, climate, tier, building age, and current contract.',
  },
  '/tools/scratch-risk-calculator': {
    metaTitle: 'Scratch Risk Calculator — the fabricating-debris math',
    metaDescription:
      'Seven inputs assemble a calibrated risk score. Glass type, age, method, debris, chemistry, wipe, pre-rinse — plus a hard-override for the catastrophic event.',
  },
  '/tools/hard-water-stain-removal': {
    metaTitle: 'Hard-Water Stain Removal Triage — the escalation ladder',
    metaDescription:
      'Six rungs of removal chemistry, gentlest first. Inputs match deposit, age, substrate, prior treatment to the right rung — with caps for coated glass.',
  },
  '/tools/ladder-safety-calculator': {
    metaTitle: 'Ladder Safety Calculator — the 4:1 rule and five overrides',
    metaDescription:
      'Nine inputs compute a safety score for the configuration. Five hard-override conditions supersede the score for the documented catastrophic failure modes.',
  },

  // ─── Pillar landing pages ──────────────────────────────────────────
  '/encyclopedia': {
    metaTitle: 'Encyclopedia — the workhorse window-washing reference',
    metaDescription:
      'Foundational reference articles: every surface, every tool, every common problem documented. Start here if you want the underlying chemistry and technique.',
  },
  '/diagnostician': {
    metaTitle: 'Diagnostician — symptom-first window cleaning guides',
    metaDescription:
      'Start with what is wrong on the glass. Match the symptom — streak, haze, spot, film — to the working diagnosis and the fix that actually resolves it.',
  },
  '/field-notes': {
    metaTitle: 'Field Notes — reported essays from working cleaners',
    metaDescription:
      'Long-form reported essays from working cleaners. Original photography. The craft, taken seriously, by the people who do it for a living.',
  },
  '/pro-track': {
    metaTitle: 'Pro Track — the trade for working cleaners',
    metaDescription:
      'Articles for working cleaners: pricing, route economics, equipment reviews, the operational decisions that matter when this is your livelihood.',
  },

  // ─── Index pages ───────────────────────────────────────────────────
  '/articles': {
    metaTitle: 'All Articles — Window Washing Guide',
    metaDescription:
      'Every article on the site, organized by editorial pillar: encyclopedia foundations, symptom diagnoses, long-form field notes, and pro-track trade pieces.',
  },
  '/blog': {
    metaTitle: 'Blog — seasonal and how-to window-washing posts',
    metaDescription:
      'Seasonal, how-to, and opinion posts on window cleaning. Shorter-form than the main articles, written when something on the route is worth writing down.',
  },
  '/news': {
    metaTitle: 'The Wire — window-cleaning & glass industry news',
    metaDescription:
      "Industry news for window cleaners. Cleaning trade press, glass-industry releases, and standards filings — paraphrased in our own words, sourced.",
  },
  '/states': {
    metaTitle: 'Window Washing by State — fifty regional field guides',
    metaDescription:
      "Window-washing field notes for all fifty states: water profiles, regional climate, the contaminants that define each market, and the cleaners' protocols.",
  },
  // s72 — Water Atlas hub. The 50 /water/<state> leaves are NOT here: like
  // city pages, they're derived at render time from state data, and the audit
  // generates their records the same way (see audit-seo-metadata.mjs §8).
  '/water': {
    metaTitle: 'Water Hardness by State — The Water Atlas',
    metaDescription:
      'Water hardness for all 50 states from figures 271 city utilities publish, in mg/L and grains per gallon, and what each hardness band means for cleaning glass.',
  },
  // s134 Phase 5b — generated from src/data/waterRankings.js.
  '/water/rankings': {
    metaTitle: 'Hardest and Softest Water by City — US Rankings',
    metaDescription:
      'US cities ranked by the hardness and dissolved solids their utilities publish, plus every city that disinfects with chloramine. Unpublished figures stay blank.',
  },
  '/contributors': {
    metaTitle: 'Contributors — the people who write Window Washing Guide',
    metaDescription:
      'The thirteen working cleaners, editors, and trade veterans who write Window Washing Guide. Their beats, their tenure, and what each of them argues for.',
  },
  '/glossary': {
    metaTitle: 'Glossary — 176 window-cleaning terms defined',
    metaDescription:
      'A working glossary of window-cleaning terminology: chemistry, technique, equipment, and the trade vocabulary that varies by region and by specialty.',
  },

  // ─── About / Trust pages ───────────────────────────────────────────
  '/editorial-standards': {
    metaTitle: 'Editorial Standards — how Window Washing Guide is made',
    metaDescription:
      'Our editorial standards: methodology transparency, correction policy, currency, conflicts of interest, and what it takes to publish under a byline here.',
  },
  '/corrections': {
    metaTitle: 'Corrections log — what Window Washing Guide got wrong',
    metaDescription:
      'Every correction to a figure or claim on Window Washing Guide: what the page said, what it says now, and the utility report or record behind the fix.',
  },
  '/methodology': {
    metaTitle: 'Methodology — where the city numbers come from',
    metaDescription:
      'Where every city figure comes from: utility water reports, EPA records, Census housing data and NOAA climate normals, plus what each number means for glass.',
  },
  '/sources': {
    metaTitle: 'Sources — references behind Window Washing Guide',
    metaDescription:
      'The published references, standards documents, and primary sources cited across Window Washing Guide articles, tools, and methodology guides.',
  },
  '/contact': {
    metaTitle: 'Contact — Window Washing Guide editorial',
    metaDescription:
      'How to reach the Window Washing Guide editorial team: corrections, tips, source submissions, and the conditions under which we update published work.',
  },
  '/submit': {
    metaTitle: 'Submit — pitch a story to Window Washing Guide',
    metaDescription:
      'How to pitch a story, essay, or methodology note to Window Washing Guide. We publish working cleaners; we want the practice, not the marketing.',
  },
  '/find-a-cleaner': {
    metaTitle: 'Find a Cleaner — local pro directory waitlist',
    metaDescription:
      'How to find a working window-cleaning operator in your area. What to ask, what to expect, and the price signals that actually predict good work.',
  },

  // ─── 404 ───────────────────────────────────────────────────────────
  '/404': {
    metaTitle: 'Not Found — Window Washing Guide',
    metaDescription:
      "The page you're looking for isn't here. The site map, the article index, and the homepage are the most-used ways back into the site.",
  },
};

// Helper: get static SEO entry by pathname, or null if not present.
// Used by useSeo hook for routes that match a static entry exactly.
export function getStaticSeo(pathname) {
  if (!pathname) return null;
  // Normalize trailing slash (except root)
  const norm = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  return STATIC_SEO[norm] || null;
}

// Helper: enforce the title brand-suffix convention. Used by useSeo when
// a frontmatter-driven page provides only a bare title.
export function withBrandSuffix(title) {
  if (!title) return SITE_NAME;
  if (title.includes(SITE_NAME)) return title;
  // Don't double-suffix if the title is already long
  if (title.length > 50) return title;
  return `${title} — ${SITE_NAME}`;
}
